import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import ReviewerActionGate from "../../../components/ReviewerActionGate";

const REVIEWER_MSG = "Reviewers are not allowed to perform this action";

const DynamicTags = ({ tags, darkenColor, openTagModalFor, itemId, isReviewer = false, isApplying = false }) => {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(1);
  
    useEffect(() => {
      if (!containerRef.current || !measureRef.current || !tags || tags.length === 0) return;
  
      const observer = new ResizeObserver(() => {
        if (!containerRef.current || !measureRef.current) return;
        const containerWidth = containerRef.current.clientWidth;
        const tagElements = Array.from(measureRef.current.children);
        
        let currentWidth = 0;
        let count = 0;
        const gap = 6; // 6px for gap-1.5
        const morePillWidth = 40; // Approx max width for "+X" pill
  
        for (let i = 0; i < tagElements.length; i++) {
          const tagWidth = tagElements[i].getBoundingClientRect().width;
          
          if (i === tagElements.length - 1) {
            // If it's the last tag, we don't need the +X pill
            if (currentWidth + tagWidth <= containerWidth) {
              count++;
            }
          } else {
            // Make sure we have enough space for the current tag + gap + +X pill
            if (currentWidth + tagWidth + gap + morePillWidth <= containerWidth) {
              count++;
              currentWidth += tagWidth + gap;
            } else {
              break; // Stop if it doesn't fit
            }
          }
        }
        
        // Always show at least 1 tag, even if it has to truncate
        setVisibleCount(Math.max(1, count));
      });
  
      observer.observe(containerRef.current);
      
      return () => observer.disconnect();
    }, [tags]);
  
    // While the list refreshes after a tag change, show a non-interactive
    // applying state instead of the (stale) add/pills view.
    if (isApplying) {
      return (
        <span className="inline-flex items-center justify-center gap-1 px-3.5 py-1 rounded-full border border-[#E4D4F5] text-[#A259FF] bg-[#F8F3FF] text-[13px] font-medium font-outfit cursor-default animate-pulse">
          Applying…
        </span>
      );
    }

    if (!Array.isArray(tags) || tags.length === 0) {
      return (
        <ReviewerActionGate active={isReviewer} place="left">
          <span
            className="inline-flex items-center justify-center gap-1 px-3.5 py-1 rounded-full border border-[#D4D4D8] text-[#52525B] bg-white whitespace-nowrap hover:border-[#A259FF] hover:text-[#A259FF] hover:bg-[#F8F3FF] text-[13px] font-medium font-outfit cursor-pointer transition-all"
            onClick={(e) => {
              e.stopPropagation();
              openTagModalFor(itemId);
            }}
          >
            + Add Tag
          </span>
        </ReviewerActionGate>
      );
    }

    return (
      <div
        className="relative flex items-center w-full h-full group/tags"
        onClick={(e) => {
          e.stopPropagation();
          if (isReviewer) return;
          openTagModalFor(itemId);
        }}
      >
        {isReviewer && (
          <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-[10000] hidden group-hover/tags:block w-max max-w-[220px] whitespace-normal rounded-md bg-[#4A4A4A] px-3 py-2 text-[12px] font-normal leading-snug text-white shadow-lg">
            {REVIEWER_MSG}
          </span>
        )}
        <div ref={containerRef} className="flex gap-1 items-center w-full overflow-hidden">
          {tags.slice(0, visibleCount).map((tag) => (
            <span
              key={tag.id}
              title={tag.name}
              className="cursor-pointer px-[6px] py-[1px] rounded-full text-[12px] font-medium truncate min-w-0"
              style={{
                color: darkenColor(tag.color || "#A259FF"),
                backgroundColor: `${tag.color || "#A259FF"}1A`,
                border: `1px solid ${tag.color || "#A259FF"}`,
              }}
            >
              {tag.name}
            </span>
          ))}
          {tags.length > visibleCount && (
            <span
              title={tags.slice(visibleCount).map((t) => t.name).join(", ")}
              className="cursor-pointer px-1 py-[1px] rounded-full text-[12px] font-medium text-[#A259FF] bg-[#F8F3FF] border border-[#A259FF] flex-shrink-0"
            >
              +{tags.length - visibleCount}
            </span>
          )}
        </div>
  
        <div 
          ref={measureRef} 
          className="absolute top-0 left-0 flex gap-1.5 pointer-events-none opacity-0 invisible -z-10"
          aria-hidden="true"
        >
          {tags.map((tag) => (
            <span
              key={`measure-${tag.id}`}
              className="px-2.5 py-0.5 rounded-full text-[12px] font-medium whitespace-nowrap border"
            >
              {tag.name}
            </span>
          ))}
        </div>
      </div>
    );
  };
  export default DynamicTags